import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef
} from 'react';
import { useUserContext } from './user_context';
import CommaonModal from '../common/Modal/CommonModal';
import { GoogleAnalyticsEnum, triggerFunction } from '../utils/google-analytics';

const COUNTDOWN_SECONDS = 60;

export const ActivityContext = createContext();

export const ActivityProvider = ({ children, inactivityTimeout = 60000 }) => {

  // reading context methods
  const { loginData, handleSignOut } = useUserContext();

  // local state initialization
  const [isActive, setIsActive] = useState(true);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [showModal, setShowModal] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  // react useRef hook
  const timeoutId = useRef(null);

  // constant function
  const resetTimer = useCallback(() => {
    clearTimeout(timeoutId.current);
    setIsActive(true);
    setLastActivityTime(Date.now());

    timeoutId.current = setTimeout(() => {
      setIsActive(false);
    }, inactivityTimeout);
  }, [inactivityTimeout]);

  useEffect(() => {
    const events = ['click', 'touchstart', 'scroll', 'mousemove', 'keydown'];

    if (!loginData?.data) {
      setIsActive(true);
      setCountdown(COUNTDOWN_SECONDS);
      return;
    }

    const handleActivity = () => {
      if (showModal) return;
      resetTimer();
    };

    events.forEach(event => window.addEventListener(event, handleActivity));
    resetTimer();

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      clearTimeout(timeoutId.current);
    };
  }, [resetTimer, loginData?.data, showModal]);

  useEffect(() => {
    if (loginData?.data && !isActive) {
      setShowModal(true);
    }
  }, [isActive, loginData]);

  useEffect(() => {
    if (!showModal) return;

    setCountdown(COUNTDOWN_SECONDS);

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          handleSignOut();
          triggerFunction(window.location.pathname, GoogleAnalyticsEnum.AUTOMATIC_LOGOUT);
          setShowModal(false);
          setIsActive(false);
          setCountdown(COUNTDOWN_SECONDS);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showModal, handleSignOut]);

  const value = {
    isActive,
    lastActivityTime,
    showModal,
    setShowModal
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
      {true && (
        <CommaonModal
          show={showModal}
          onCloseModal={() => setShowModal(false)}
          title="Session Out Warning"
          timer={countdown}
          onStayLoggedIn={() => {
            setShowModal(false);
            setCountdown(COUNTDOWN_SECONDS);
            resetTimer();
            triggerFunction(GoogleAnalyticsEnum.BUTTON, GoogleAnalyticsEnum.STAY_LOGGEDIN_BUTTON);
          }}
          onLogout={() => {
            handleSignOut();
            triggerFunction(GoogleAnalyticsEnum.BUTTON, GoogleAnalyticsEnum.LOGOUT_BUTTON);
            setShowModal(false);
            setIsActive(false);
            setCountdown(COUNTDOWN_SECONDS);
          }}
        />

      )}
    </ActivityContext.Provider>
  );
};

export const useActivityContext = () => useContext(ActivityContext);
